/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { HeaderCardComponent } from '../../header-card/header-card.component';
import { MatCard } from '@angular/material/card';
import { TournamentTeamsComponent } from '../../tournament-teams/tournament-teams.component';
import {
  KoStandingsEndpointService,
  QualificationTeamScoreDto,
  TeamDto,
  TournamentEndpointService,
  TournamentOverviewDto,
  TournamentQualificationMatchDto,
} from '@api';
import { catchError, firstValueFrom, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TournamentScoreTableComponent } from '../../tournament-score-table/tournament-score-table.component';
import { QualificationMatchLineComponent } from '../../qualification-matches/qualification-match-line/qualification-match-line.component';
import { DatePipe } from '@angular/common';
import { ConfirmationService } from '../../../services/confirmation.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { PickQualifiedTeamsComponent } from './pick-qualified-teams/pick-qualified-teams.component';
import { RouterModule } from '@angular/router';
import {
  MarkTeamDrinksPickedUpDto,
  QualificationMatchesComponent,
} from '../../qualification-matches/qualification-matches.component';
import { CopyLinkService } from '../../../services/copy-link.service';

@Component({
  selector: 'app-tournament-qualification-phase',
  standalone: true,
  imports: [
    MatButton,
    HeaderCardComponent,
    MatCard,
    TournamentTeamsComponent,
    TournamentScoreTableComponent,
    QualificationMatchLineComponent,
    DatePipe,
    MatIcon,
    MatTooltip,
    RouterModule,
    QualificationMatchesComponent,
  ],
  templateUrl: './tournament-qualification-phase.component.html',
  styleUrl: './tournament-qualification-phase.component.scss',
})
export class TournamentQualificationPhaseComponent implements OnInit, OnDestroy {
  tournamentService = inject(TournamentEndpointService);
  koStandingsService = inject(KoStandingsEndpointService);
  snackBar = inject(MatSnackBar);
  confirmService = inject(ConfirmationService);
  matDialog = inject(MatDialog);
  copyLinkService = inject(CopyLinkService);

  tournamentId = input<number>();
  teamDtos = signal<Map<number, TeamDto>>(new Map());
  scoreDtos = signal<QualificationTeamScoreDto[]>([]);
  qualificationMatches = signal<TournamentQualificationMatchDto[]>([]);
  qualificationMatchesFinished = computed(() => {
    return (
      !this.qualificationMatches().some(match => match.winnerPoints === null) &&
      this.qualificationMatches().length > 0
    );
  });
  koPhaseStarted = computed(() => {
    return this.tournament()?.allKoMatches !== undefined && this.tournament()!.allKoMatches > 0;
  });
  tournament = signal<TournamentOverviewDto | undefined>(undefined);

  infoElement = viewChild<ElementRef>('qualifiedInfo');
  matchListElement = viewChild<ElementRef>('matchList');

  refreshInterval: number | undefined;

  ngOnInit(): void {
    this.loadTournament();
    this.loadData();

    this.refreshInterval = window.setInterval(() => {
      this.loadData();
    }, 3000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval !== undefined) {
      clearInterval(this.refreshInterval);
    }
  }

  loadScores(): void {
    if (this.tournamentId() !== undefined) {
      this.tournamentService
        .getTournamentQualificationScoreTable(this.tournamentId()!)
        .pipe(
          catchError(error => {
            console.error(error);
            this.snackBar.open('Failed to load qualification scores: ' + error.message, 'Close');
            return of([]);
          }),
        )
        .subscribe((scores: QualificationTeamScoreDto[]) => {
          this.scoreDtos.set(scores);
        });
    }
  }

  loadQualificationMatches(): void {
    if (this.tournamentId() !== undefined) {
      this.tournamentService
        .getQualificationMatches(this.tournamentId()!)
        .pipe(
          catchError(error => {
            console.error(error);
            this.snackBar.open('Failed to load qualification matches: ' + error.message, 'Close');
            return of([]);
          }),
        )
        .subscribe((matches: TournamentQualificationMatchDto[]) => {
          this.qualificationMatches.set(matches);
        });
    }
  }

  loadTeams(): void {
    if (this.tournamentId() === undefined) {
      return;
    }

    this.tournamentService
      .getTournamentTeams(this.tournamentId()!)
      .pipe(
        catchError(error => {
          console.error(error);
          this.snackBar.open('Failed to load teams: ' + error.message, 'Close');
          return of([]);
        }),
      )
      .subscribe((teams: TeamDto[]) => {
        const mapped = new Map(teams.map(t => [t.id!, t]));
        this.teamDtos.set(mapped);
      });
  }

  private loadData() {
    this.loadScores();
    this.loadQualificationMatches();
    this.loadTeams();
  }

  async checkInTeam(team: QualificationTeamScoreDto): Promise<void> {
    if (!this.tournamentId() || !team?.id) {
      console.log('got invalid tournament or participant');
      return;
    }

    const answer = await this.confirmService.openConfirmationDialog(
      `check in team ${team.name}?`,
      'This action cannot be undone.',
      false,
      'Check in',
    );

    if (answer) {
      firstValueFrom(
        this.tournamentService.markTeamAsReady(this.tournamentId()!, team.id).pipe(
          catchError(error => {
            console.error(error);
            this.snackBar.open('Failed to check in team: ' + error.message, 'Close');
            return of([]);
          }),
        ),
      ).then(() => this.loadData());
    }
  }

  async markTeamDrinksPickedUp(
    markTeamDrinksPickedUpDto: MarkTeamDrinksPickedUpDto,
  ): Promise<void> {
    if (!this.tournamentId() || !markTeamDrinksPickedUpDto.participantDto?.teamId) {
      console.log('got invalid tournament or participant');
      return;
    }

    const answer = await this.confirmService.openConfirmationDialog(
      `mark drinks as picked up for team '${markTeamDrinksPickedUpDto.participantDto.name}?'`,
      'This action cannot be undone.',
      false,
      'Mark as picked up',
    );

    if (answer) {
      firstValueFrom(
        this.tournamentService
          .updateQualificationRound(this.tournamentId()!, markTeamDrinksPickedUpDto.matchId!, {
            drinksPickup: { teamId: markTeamDrinksPickedUpDto.participantDto.teamId! },
          })
          .pipe(
            catchError(error => {
              console.error(error);
              this.snackBar.open(
                `Failed to mark drinks as collected for team '${markTeamDrinksPickedUpDto.participantDto.name}': ${error.message}`,
                'Close',
              );
              return of([]);
            }),
          ),
      ).then(() => {
        this.snackBar.open(
          `Marked drinks for team ${markTeamDrinksPickedUpDto.participantDto.name} as collected`,
          'Close',
          { duration: 2500 },
        );
        this.loadData();
      });
    }
  }

  onMatchUpdate(updatedMatch: TournamentQualificationMatchDto): void {
    this.qualificationMatches.update(matches => {
      for (let i = 0; i < matches.length; i++) {
        if (matches[i].id === updatedMatch.id) {
          matches[i] = updatedMatch;
          break;
        }
      }

      return matches;
    });
  }

  async generateQualificationMatches() {
    if (this.tournamentId() !== undefined) {
      const answer = await this.confirmService.openConfirmationDialog(
        `generate qualification matches?`,
        'After you generate the matches, you cannot create or delete any more teams.',
        false,
        'Generate',
      );

      if (answer) {
        firstValueFrom(
          this.tournamentService.generateQualificationMatches(this.tournamentId()!).pipe(
            catchError(error => {
              console.error(error.error);
              this.snackBar.open(
                'Failed to generate qualification matches: ' + error.error,
                'Close',
              );
              return of([]);
            }),
          ),
        ).then(() => this.loadData());
      }
    }
  }

  get edgeTeamsWithSamePoints(): TournamentQualificationMatchDto[] {
    const teamOn16place = this.scoreDtos()[15];
    return this.scoreDtos().filter(team => team.position === teamOn16place.position);
  }

  get guaranteedQualifiedTeams(): QualificationTeamScoreDto[] {
    const first16Teams = this.scoreDtos().slice(0, 16);
    const edgeTeams = this.edgeTeamsWithSamePoints;
    return first16Teams.filter(team => !edgeTeams.some(edgeTeam => edgeTeam.id === team.id));
  }

  async startKoPhase() {
    let qualifiedTeams: number[] = [];
    if (this.edgeTeamsWithSamePoints.length === 1) {
      qualifiedTeams = this.guaranteedQualifiedTeams.map(team => team.id!);
      qualifiedTeams.push(this.edgeTeamsWithSamePoints[0].id!);
    } else if (this.guaranteedQualifiedTeams.length + this.edgeTeamsWithSamePoints.length === 16) {
      qualifiedTeams = this.guaranteedQualifiedTeams.map(team => team.id!);
      this.edgeTeamsWithSamePoints.forEach(team => qualifiedTeams.push(team.id!));
    } else {
      qualifiedTeams = await this.askUserToPickTeams();
    }
    this.koStandingsService
      .generateKoMatches(this.tournamentId()!, {
        qualifiedTeamIds: qualifiedTeams,
      })
      .subscribe(() => {
        this.loadData();
        this.loadTournament();
      });
  }

  async askUserToPickTeams(): Promise<number[]> {
    const dialogRef = this.matDialog.open(PickQualifiedTeamsComponent, {
      height: '80vh',
      width: '90vw',
      maxWidth: '100%',
      maxHeight: '100%',
    });
    dialogRef.componentInstance.edgeTeamsWithSamePoints.set(this.edgeTeamsWithSamePoints);
    dialogRef.componentInstance.guaranteedQualifiedTeams.set(this.guaranteedQualifiedTeams);
    dialogRef.componentInstance.guaranteedNotQualifiedTeams.set(this.guaranteedNotQualifiedTeams);

    return await firstValueFrom(dialogRef.afterClosed()).then((result: number[]) => {
      return result;
    });
  }

  get guaranteedNotQualifiedTeams(): QualificationTeamScoreDto[] {
    const after16Teams = this.scoreDtos().slice(16);
    const edgeTeams = this.edgeTeamsWithSamePoints;
    return after16Teams.filter(team => !edgeTeams.some(edgeTeam => edgeTeam.id === team.id));
  }

  private loadTournament() {
    if (this.tournamentId() !== undefined) {
      this.tournamentService
        .getTournamentOverview(this.tournamentId()!)
        .pipe(
          catchError(error => {
            console.error(error);
            this.snackBar.open('Failed to load tournament: ' + error.message, 'Close');
            return of(undefined);
          }),
        )
        .subscribe(tournament => {
          this.tournament.set(tournament);
        });
    }
  }

  openCopyPublicLinkDialog() {
    this.copyLinkService.openDialog({
      link:
        window.origin +
        '/#/tournaments/' +
        this.tournamentId() +
        '/live?token=' +
        this.tournament()?.publicAccessToken,
      title: 'Public link to qualification phase',
    });
  }

  getStartKoPhaseButtonTooltip(): string {
    if (this.koPhaseStarted()) {
      return 'KO phase already started';
    }

    if (!this.qualificationMatchesFinished()) {
      return 'Qualification matches not finished';
    }

    return 'Start KO phase';
  }
}
